import { useState, useContext } from "react";
import { Button, Form, Space, Row, Col, Upload, Typography } from "antd";
import { LoadingOutlined, PlusOutlined, FileOutlined } from "@ant-design/icons";

import { ClaimContext } from "../../../context/ClaimContext";

interface StepProps {
  gotoStep: Function;
  current: number;
  submit: Function;
  loading?: boolean;
}

const SINGLE_SLOTS = [
  { key: "DeathCertificate", label: "Death Certificate", required: true },
  { key: "IdDocumentOfDeceased", label: "ID Document of Deceased", required: true },
  { key: "BI1663Certificate", label: "BI - 1663 Certificate", required: true },
  { key: "MarriageCertificate", label: "Marriage Certificate", required: false },
  { key: "IdDocumentOfSpouse", label: "ID Document of Spouse", required: false },
  {
    key: "IdDocumentOfDependant",
    label: "ID/Birth Certificate of Dependant",
    required: false,
  },
] as const;

const Documents: React.FC<StepProps> = ({
  gotoStep,
  current,
  submit,
  loading,
}) => {
  const { setClaimData, claimData } = useContext(ClaimContext);
  const [imageUrls, setImageUrls] = useState<{ [key: string]: string }>({});

  const docs: any[] = Array.isArray(claimData?.Documents)
    ? claimData.Documents
    : [];

  const hasDoc = (fieldName: string) =>
    !!imageUrls[fieldName] || docs.some((d) => d?.Name === fieldName);

  const otherDocs = docs.filter(
    (d) =>
      d?.Name === "OtherDocument" ||
      String(d?.Name || "").startsWith("OtherDocument_")
  );

  const getBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  const uploadButton = (
    <button style={{ border: 0, background: "none" }} type="button">
      {loading ? <LoadingOutlined /> : <PlusOutlined />}
      <div style={{ marginTop: 8 }}>Upload</div>
    </button>
  );

  const handleChange = async (info: any, fieldName: string) => {
    const nativeFile = info.file.originFileObj ?? info.file;
    if (!nativeFile) return;
    try {
      const base64 = await getBase64(nativeFile);

      setImageUrls((prevState: any) => ({
        ...prevState,
        [fieldName]: base64,
      }));

      setClaimData((prev: any) => {
        const rest = (prev.Documents ?? []).filter(
          (d: any) => d.Name !== fieldName
        );
        return {
          ...prev,
          Documents: [...rest, { Name: fieldName, Value: base64 }],
        };
      });
    } catch (error) {
      console.error("Error converting file to base64:", error);
    }
  };

  const handleOtherChange = async (info: any) => {
    const file = info.file;
    if (!file || file.status === "removed") return;
    const nativeFile = file.originFileObj ?? file;
    if (!(nativeFile instanceof Blob)) return;
    const uid = file.uid;
    if (uid && imageUrls[`__uid_${uid}`]) return;

    try {
      const base64 = await getBase64(nativeFile as File);
      setClaimData((prev: any) => {
        const existing = prev.Documents ?? [];
        const otherNames = existing.filter(
          (d: any) =>
            d?.Name === "OtherDocument" ||
            String(d?.Name || "").startsWith("OtherDocument_")
        );
        const fieldName =
          otherNames.length === 0
            ? "OtherDocument"
            : `OtherDocument_${otherNames.length + 1}`;

        setImageUrls((prevUrls) => ({
          ...prevUrls,
          [fieldName]: base64,
          ...(uid ? { [`__uid_${uid}`]: "1" } : {}),
        }));

        return {
          ...prev,
          Documents: [...existing, { Name: fieldName, Value: base64 }],
        };
      });
    } catch (error) {
      console.error("Error converting file to base64:", error);
    }
  };

  const tailLayout = {
    labelCol: { span: 8 },
    wrapperCol: { offset: 8, span: 8 },
  };

  return (
    <>
      <Space
        size={"large"}
        direction="vertical"
        style={{ width: "100%", textAlign: "center", margin: "0 0 16px 0" }}
      >
        <h2 style={{ margin: "16px 0", color: "#f04f05" }}>
          Upload The Required Documents.
        </h2>
        <p style={{ margin: "16px 0" }}>
          To finalise the claim proses upload all required documents.
        </p>
      </Space>

      <Row style={{ width: "80%", marginLeft: "10%" }}>
        {SINGLE_SLOTS.map((slot) => (
          <Col span={6} style={{ textAlign: "center" }} key={slot.key}>
            <p>
              {slot.label}
              {slot.required ? <span style={{ color: "red" }}>*</span> : null}
            </p>
            <Upload
              name="avatar"
              listType="picture-card"
              className="avatar-uploader"
              showUploadList={false}
              beforeUpload={() => false}
              onChange={(info: any) => handleChange(info, slot.key)}
            >
              {hasDoc(slot.key) ? (
                <FileOutlined style={{ fontSize: 64, color: "#1890ff" }} />
              ) : (
                uploadButton
              )}
            </Upload>
          </Col>
        ))}

        <Col span={6} style={{ textAlign: "center" }}>
          <p>Other Document (multiple)</p>
          <Upload
            name="avatar"
            listType="picture-card"
            className="avatar-uploader"
            showUploadList={false}
            multiple
            beforeUpload={() => false}
            onChange={handleOtherChange}
          >
            {uploadButton}
          </Upload>
          {(otherDocs.length > 0 ||
            Object.keys(imageUrls).some((k) => k.startsWith("OtherDocument"))) && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {
                new Set([
                  ...otherDocs.map((d) => d.Name),
                  ...Object.keys(imageUrls).filter((k) =>
                    k.startsWith("OtherDocument")
                  ),
                ]).size
              }{" "}
              other file(s) attached
            </Typography.Text>
          )}
        </Col>
      </Row>

      <Form.Item {...tailLayout}>
        <Space direction="vertical" style={{ marginTop: 20, width: "100%" }}>
          <Button
            loading={loading}
            style={{ marginBottom: 10 }}
            block
            size="large"
            onClick={() => {
              submit().then((result: any) => {
                if (result) {
                  gotoStep(7 + 1);
                } else {
                  gotoStep(7 + 2);
                }
              });
            }}
          >
            Submit
          </Button>
        </Space>
      </Form.Item>
    </>
  );
};

export default Documents;
